// timezone-standardization
// eslint-disable-next-line no-restricted-imports
import { sub, milliseconds, startOfMonth, endOfMonth, setMilliseconds, format } from "date-fns";
import { utcToZonedTime, formatInTimeZone } from "date-fns-tz";
export var PinStatus;
(function(PinStatus) {
    PinStatus["PINNED"] = "pinned";
    PinStatus["PINNED_IN_DIFFERENT_TAB"] = "pinned_in_different_tab";
    PinStatus["UNPINNED"] = "unpinned";
})(PinStatus || (PinStatus = {}));
/**
 * Determine whether a given time range is relative
 */ export function isRelativeTimeRange(timeRange) {
    return timeRange.pastDuration !== undefined;
}
/**
 * Determine whether a given time range is absolute
 */ export function isAbsoluteTimeRange(timeRange) {
    return timeRange.start !== undefined && timeRange.end !== undefined;
}
/**
 * Returns an absolute time range from a RelativeTimeRange.
 */ export function toAbsoluteTimeRange(timeRange) {
    // Set milliseconds to 0 when generating a new date to avoid propagating a somewhat random
    // milliseconds value to the new date that isn't user visible in the UX (e.g. the time picker) and
    // could potentially lead to some weird edge cases.
    const end = timeRange.end ?? setMilliseconds(new Date(), 0);
    return {
        start: sub(end, parseDurationString(timeRange.pastDuration)),
        end
    };
}
export var DURATION_UNIT;
(function(DURATION_UNIT) {
    DURATION_UNIT["MILLISECOND"] = "ms";
    DURATION_UNIT["SECOND"] = "s";
    DURATION_UNIT["MINUTE"] = "m";
    DURATION_UNIT["HOUR"] = "h";
    DURATION_UNIT["DAY"] = "d";
    DURATION_UNIT["WEEK"] = "w";
    DURATION_UNIT["MONTH"] = "M";
    DURATION_UNIT["YEAR"] = "y";
})(DURATION_UNIT || (DURATION_UNIT = {}));
/**
 * Regular expression pattern for parsing duration strings.
 * The pattern matches strings in the format: "1y2mo3w4d5h6m7s8ms"
 * where each component represents a duration unit (years, weeks, days, hours, minutes, seconds, milliseconds).
 * The captured groups can be used to extract the individual duration components.
 */ const DURATION_REGEX = new RegExp(`^(?:(\\d+)${"y"})?(?:(\\d+)${"M"})?(?:(\\d+)${"w"})?(?:(\\d+)${"d"})?(?:(\\d+)${"h"})?(?:(\\d+)${"m"})?(?:(\\d+)s)?(?:(\\d+)${"ms"})?$`);
/**
 * Parses a DurationString into a Duration object with numeric values that can
 * be used to do Date math. Throws if not a valid duration string.
 */ export function parseDurationString(durationString) {
    const matches = DURATION_REGEX.exec(durationString);
    if (matches === null) {
        throw new Error(`Invalid duration string '${durationString}'`);
    }
    return {
        years: parseInt(matches[1] ?? '0'),
        months: parseInt(matches[2] ?? '0'),
        weeks: parseInt(matches[3] ?? '0'),
        days: parseInt(matches[4] ?? '0'),
        hours: parseInt(matches[5] ?? '0'),
        minutes: parseInt(matches[6] ?? '0'),
        seconds: parseInt(matches[7] ?? '0') + parseInt(matches[8] ?? '0') / 1000
    };
}
/**
 * Converts a DurationString to seconds, rounding down.
 */ export function getDurationStringSeconds(durationString) {
    if (durationString === undefined || durationString === '') return undefined;
    const duration = parseDurationString(durationString);
    return Math.floor(milliseconds(duration) / 1000);
}
/**
 * Returns true if the given string is a valid DurationString.
 */ export function isDurationString(maybeDuration) {
    if (maybeDuration === '') return false;
    return DURATION_REGEX.test(maybeDuration);
}
const DEFAULT_STEP_MS = 15000;
const ROUNDED_STEP_INTERVALS = [
    // max: 1.5s
    {
        maxMs: 1500,
        roundedStepMs: 1000,
        display: '1s'
    },
    // max: 3.5s
    {
        maxMs: 3500,
        roundedStepMs: 2000,
        display: '2s'
    },
    // max: 7.5s
    {
        maxMs: 7500,
        roundedStepMs: 5000,
        display: '5s'
    },
    // max: 12.5s
    {
        maxMs: 12500,
        roundedStepMs: 10000,
        display: '10s'
    },
    // max: 17.5s
    {
        maxMs: 17500,
        roundedStepMs: 15000,
        display: '15s'
    },
    // max: 25s
    {
        maxMs: 25000,
        roundedStepMs: 20000,
        display: '20s'
    },
    // max: 45s
    {
        maxMs: 45000,
        roundedStepMs: 30000,
        display: '30s'
    },
    // max: 1.5m
    {
        maxMs: 90000,
        roundedStepMs: 60000,
        display: '1m'
    },
    // max: 3.5m
    {
        maxMs: 210000,
        roundedStepMs: 120000,
        display: '2m'
    },
    // max: 7.5m
    {
        maxMs: 450000,
        roundedStepMs: 300000,
        display: '5m'
    },
    // max: 12.5m
    {
        maxMs: 750000,
        roundedStepMs: 600000,
        display: '10m'
    },
    // max: 12.5m
    {
        maxMs: 1050000,
        roundedStepMs: 900000,
        display: '15m'
    },
    // max: 25m
    {
        maxMs: 1500000,
        roundedStepMs: 1200000,
        display: '20m'
    },
    // max: 45m
    {
        maxMs: 2700000,
        roundedStepMs: 1800000,
        display: '30m'
    },
    // max: 1.5h
    {
        maxMs: 5400000,
        roundedStepMs: 3600000,
        display: '1h'
    },
    // max: 2.5h
    {
        maxMs: 9000000,
        roundedStepMs: 7200000,
        display: '2h'
    },
    // max: 4.5h
    {
        maxMs: 16200000,
        roundedStepMs: 10800000,
        display: '3h'
    },
    // max: 9h
    {
        maxMs: 32400000,
        roundedStepMs: 21600000,
        display: '6h'
    },
    // max: 1d
    {
        maxMs: 86400000,
        roundedStepMs: 43200000,
        display: '12h'
    },
    // max: 1w
    {
        maxMs: 604800000,
        roundedStepMs: 86400000,
        display: '1d'
    },
    // max: 3w
    {
        maxMs: 1814400000,
        roundedStepMs: 604800000,
        display: '1w'
    },
    // max: 6w
    {
        maxMs: 3628800000,
        roundedStepMs: 2592000000,
        display: '30d'
    },
    // max: 2y
    {
        maxMs: 63072000000,
        roundedStepMs: 31536000000,
        display: '1y'
    }
];
/**
 * Round interval to clearer increments
 */ export function roundStepInterval(stepMs) {
    for (const { maxMs, roundedStepMs } of ROUNDED_STEP_INTERVALS){
        if (stepMs < maxMs) {
            return roundedStepMs;
        }
    }
    return DEFAULT_STEP_MS;
}
/**
 * Gets a suggested step/interval size for a time range based on the width of a visual component.
 * TODO: optimize step for better performance (sc-98578)
 */ export function getSuggestedStepMs(timeRange, width) {
    const queryRangeMs = timeRange.end.valueOf() - timeRange.start.valueOf();
    const stepMs = Math.floor(queryRangeMs / width);
    return roundStepInterval(stepMs);
}
/**
 * Returns true if the given value is a valid Date object.
 */ export function isValidDate(d) {
    return d instanceof Date && !isNaN(d.valueOf());
}
/**
 * Returns true if the specified time zone value indicates it is using the user's
 * local, browser-based time zone. If not specified, the time zone is assumed to be
 * local.
 */ export function isLocalTimeZone(timeZone) {
    const lowerTimeZone = normalizeTimeZone(timeZone).toLowerCase();
    return lowerTimeZone === 'local' || lowerTimeZone === 'browser';
}
/**
 * Normalizes some inconsistencies in time zones to ensure they will work properly
 * when using date-related APIs. Will return default of "local" if time zone
 * not specified.
 */ export function normalizeTimeZone(timeZone = 'local') {
    return timeZone === 'utc' ? 'UTC' : timeZone;
}
/**
 * Returns a time range representing the start and end of the specified month.
 * @param monthDate Date within the current month. If not specified, the current date will be used.
 * @param timeZone Time zone to be used when calculating the start/end of the month.
 */ export function getMonthTimeRange(monthDate = new Date(), timeZone) {
    const normalizedTimeZone = normalizeTimeZone(timeZone);
    const localTimeZone = isLocalTimeZone(normalizedTimeZone);
    // Ensure we are grabbing the start of the correct month for the specified time zone.
    // Otherwise, we could accidentally grab the wrong month near the edge of month
    // boundaries.
    const zonedMonthDate = localTimeZone ? monthDate : utcToZonedTime(monthDate, normalizedTimeZone);
    // Grab start/end and convert them to the correct time zone, when applicable.
    const start = localTimeZone ? startOfMonth(zonedMonthDate) : utcToZonedTime(startOfMonth(zonedMonthDate), normalizedTimeZone);
    const end = localTimeZone ? endOfMonth(zonedMonthDate) : utcToZonedTime(endOfMonth(zonedMonthDate), normalizedTimeZone);
    return {
        start,
        end: setMilliseconds(end, 0)
    };
}
export var DATE_FORMAT;
(function(DATE_FORMAT) {
    DATE_FORMAT["YEAR"] = "yyyy";
    DATE_FORMAT["SHORT"] = "MM/dd/yyyy";
    DATE_FORMAT["MEDIUM"] = "MMM dd, yyyy";
    DATE_FORMAT["MEDIUM_NO_YEAR"] = "MMM dd";
})(DATE_FORMAT || (DATE_FORMAT = {}));
export var TIME_FORMAT;
(function(TIME_FORMAT) {
    TIME_FORMAT["SHORT_12"] = "hh:mm aa";
    TIME_FORMAT["SHORT_24"] = "HH:mm";
    TIME_FORMAT["MEDIUM_12"] = "hh:mm:ss aa";
    TIME_FORMAT["MEDIUM_24"] = "HH:mm:ss";
})(TIME_FORMAT || (TIME_FORMAT = {}));
export const DATE_TIME_FORMAT_12 = `${"MM/dd/yyyy"} ${"hh:mm:ss aa"}`;
export const DATE_TIME_FORMAT_12_SHORT_TIME = `${"MM/dd/yyyy"} ${"hh:mm aa"}`;
/**
 * Formats the date using the specified time zone.
 */ export function formatWithTimeZone(date, formatString, timeZone) {
    const normalizedTimeZone = normalizeTimeZone(timeZone);
    if (isLocalTimeZone(normalizedTimeZone)) {
        return format(date, formatString);
    } else {
        return formatInTimeZone(date, normalizedTimeZone, formatString);
    }
}
export const MINUTE_MS = 60 * 1_000;
export const HOUR_MS = 60 * MINUTE_MS;
export const DAY_MS = 86400000;
export const MONTH_MS = 2629440000;
export const YEAR_MS = 31536000000;

//# sourceMappingURL=time.js.map